// AcceptInvitePage - accept an org/admin invite link: a new account is created
// with the password chosen here, an existing one gains the membership and keeps
// the password it has.
//
// The page cannot tell which of those it is looking at, and deliberately does
// not try. The lookup endpoint is unauthenticated and used to answer it, which
// handed the account-existence answer to whoever held the token - for any
// address, at request speed. The password field is therefore optional and the
// copy says who needs to fill it in: the server validates it only once it knows
// the account is new, and its accept response carries `existing_account`, which
// is what routes the two apart afterwards.

import { CheckCircle2, Loader2 } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { AuthShell } from '@/components/AuthShell';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { ApiError, api, type InviteLookup } from '@/lib/api';

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const toast = useToast();
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [info, setInfo] = useState<InviteLookup | null>(null);
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .inviteLookup(token)
      .then(setInfo)
      .catch((err) => {
        // A consumed/expired invite is HTTP 410 (distinct from a bad token),
        // so the page can say "expired" rather than "invalid".
        if (err instanceof ApiError && err.status === 410) setExpired(true);
        setInfo(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (displayName.trim() === '') {
      toast.error('Please enter your name');
      return;
    }
    // Checked only when one was typed. An empty field is the existing-account
    // case, and the server refuses it with "password (min 12) required for a
    // new account" if it turns out not to be - which is the one message that
    // can name the reason, since only the server knows whether the account
    // exists.
    if (password !== '') {
      if (password !== confirm) {
        toast.error('Passwords do not match');
        return;
      }
      if (password.length < 12) {
        toast.error('Password must be at least 12 characters');
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await api.acceptInvite(token, displayName.trim(), password);
      setDone(true);
      // The server's response is authoritative: an existing account gets a
      // membership only (no session), so route it to normal sign-in; a new
      // account already has a session cookie from accept.
      if (res.existing_account) {
        nav('/login');
      } else {
        await refresh();
        nav('/dashboard');
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to accept invite');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <AuthShell title="Loading invite…">
        <div className="card p-8 text-center text-sm text-ink-400">
          <Loader2 className="w-5 h-5 animate-spin text-ink-500 mx-auto mb-3" />
          Verifying your invite link.
        </div>
      </AuthShell>
    );
  }
  if (expired) {
    return (
      <AuthShell title="Invite expired">
        <div className="card p-8 text-center text-sm text-ink-300">
          This invite has expired or already been used. Ask your administrator for a fresh one.
        </div>
      </AuthShell>
    );
  }
  if (!token || !info) {
    return (
      <AuthShell title="Invite not found">
        <div className="card p-8 text-center text-sm text-ink-300">
          <p>This invite link is invalid.</p>
          <p className="mt-3">
            <Link to="/login" className="link">Sign in instead</Link>
          </p>
        </div>
      </AuthShell>
    );
  }
  if (done) {
    return (
      <AuthShell title="Invite accepted">
        <div className="card p-8 text-center text-sm text-ink-300">
          <Loader2 className="w-5 h-5 animate-spin text-accent-400 mx-auto mb-3" />
          Redirecting…
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell subtitle={info.email} title="Accept invite">
      <div className="card p-7">
        <p className="text-sm text-ink-400 mb-6">
          {info.org_name ? (
            <>
              You've been invited to <span className="text-ink-100 font-medium">{info.org_name}</span>{' '}
              as <span className="text-accent-300 font-medium">{(info.role === 'org_admin' || info.role === 'manager') ? 'Management' : 'Member'}</span>.
            </>
          ) : (
            <>You've been invited as a Foresee administrator.</>
          )}
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="label" htmlFor="invite-name">Your name</label>
            <input
              id="invite-name"
              type="text"
              autoComplete="name"
              required
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Full name"
            />
          </div>
          {/* Optional, and the helper says who it is for. Whether this address
              already has an account is the one thing the page is not told, so
              requiring a password would make an existing invitee invent one
              that accept discards, and omitting the field would leave a new
              one with no way in. */}
          <div>
            <label className="label" htmlFor="invite-password">Password</label>
            <input
              id="invite-password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 12 characters"
            />
            <p className="text-xs text-ink-500 mt-1.5">
              Set one if this is your first Foresee account. If you already have one, leave this
              blank - accepting only adds you to the organization and your password stays the same.
            </p>
          </div>
          {password !== '' && (
            <div>
              <label className="label" htmlFor="invite-confirm">Confirm password</label>
              <input
                id="invite-confirm"
                type="password"
                autoComplete="new-password"
                minLength={12}
                className="input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              {confirm && password !== confirm && (
                <div className="error-text">Passwords don't match.</div>
              )}
            </div>
          )}
          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Accept invite
          </button>
        </form>
      </div>
    </AuthShell>
  );
}
